import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Flame, Mail, Heart, Sparkles, BookOpen } from "lucide-react";
import { format } from "date-fns";
import TodoList from "@/components/TodoList";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, streak: 0 });
  const [letters, setLetters] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/gratitude/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/letters").then(({ data }) => setLetters(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/stories").then(({ data }) => setStories(data.slice(0, 4))).catch(() => {});
  }, []);

  const upcomingBookings = bookings.filter((b) => new Date(b.slot) > new Date());
  const pendingLetters = letters.filter((l) => l.status === "scheduled");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="page-dashboard">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
        <span className="editorial-rule" />Your space
      </div>
      <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight">
        Welcome back, <em className="italic text-mindora-red">{user?.name?.split(" ")[0] || "friend"}.</em>
      </h1>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="dashboard-grid">
        {/* Streak hero */}
        <div className="md:col-span-7 rounded-3xl bg-mindora-ink text-mindora-bg p-10 shadow-clay border border-mindora-ink">
          <div className="flex items-center gap-2 text-mindora-clay text-xs uppercase tracking-[0.22em]">
            <Flame size={14} /> Gratitude streak
          </div>
          <div className="font-serif text-7xl mt-4" data-testid="dashboard-streak">{stats.streak}<span className="text-3xl text-mindora-bg/60"> days</span></div>
          <p className="mt-3 text-mindora-bg/70 max-w-md">
            Small things, collected slowly. {stats.total} {stats.total === 1 ? "note" : "notes"} in your jar so far.
          </p>
          <Link to="/gratitude" className="inline-block mt-7 text-sm border-b border-mindora-clay pb-0.5 hover:text-mindora-clay">
            Add today&apos;s note →
          </Link>
        </div>

        {/* Letters */}
        <div className="md:col-span-5 rounded-3xl bg-mindora-panel border border-mindora-line p-8 shadow-clay">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <Mail size={14} /> Letters waiting
          </div>
          <div className="font-serif text-5xl text-mindora-ink mt-4" data-testid="dashboard-letters-count">{pendingLetters.length}</div>
          <ul className="mt-4 space-y-2.5">
            {pendingLetters.slice(0, 3).map((l) => (
              <li key={l.id} className="text-sm text-mindora-ink/80 flex justify-between gap-3">
                <span className="truncate">{l.subject}</span>
                <span className="text-xs text-mindora-muted shrink-0">{format(new Date(l.deliver_at), "MMM d")}</span>
              </li>
            ))}
            {pendingLetters.length === 0 && <li className="text-sm italic text-mindora-muted">No letters scheduled.</li>}
          </ul>
          <Link to="/future-self" className="inline-block mt-5 text-sm text-mindora-red border-b border-mindora-red pb-0.5 hover:text-mindora-ink hover:border-mindora-ink">
            Write a new one →
          </Link>
        </div>

        {/* To-do list */}
        <div className="md:col-span-7">
          <TodoList />
        </div>

        {/* Upcoming sessions */}
        <div className="md:col-span-5 rounded-3xl bg-mindora-panel border border-mindora-line p-8 shadow-clay">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <Heart size={14} /> Upcoming sessions
          </div>
          <ul className="mt-5 space-y-3" data-testid="dashboard-bookings">
            {upcomingBookings.slice(0, 3).map((b) => (
              <li key={b.id} className="border-b border-mindora-line pb-3 last:border-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg text-mindora-ink leading-tight">{b.therapist_name}</div>
                    <div className="text-xs text-mindora-muted mt-0.5">{format(new Date(b.slot), "PPpp")}</div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Cancel session with ${b.therapist_name}?`)) return;
                      try {
                        const { api } = await import("@/lib/api");
                        await api.post(`/bookings/${b.id}/cancel`, {});
                        const { data } = await api.get("/bookings");
                        setBookings(data);
                      } catch (_e) {
                        /* UI stays as-is */
                      }
                    }}
                    data-testid={`dashboard-cancel-${b.id}`}
                    className="text-[11px] text-mindora-muted hover:text-mindora-red whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </li>
            ))}
            {upcomingBookings.length === 0 && <li className="text-sm italic text-mindora-muted">No sessions yet. Browse counselors.</li>}
          </ul>
          <Link to="/directory" className="inline-block mt-5 text-sm text-mindora-red border-b border-mindora-red pb-0.5 hover:text-mindora-ink hover:border-mindora-ink">
            Book a session →
          </Link>
        </div>

        {/* Quick actions */}
        <div className="md:col-span-7 rounded-3xl bg-mindora-bg border border-mindora-line p-8">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <Sparkles size={14} /> Try today
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <Link to="/write" data-testid="dashboard-quick-write" className="rounded-2xl border border-mindora-line p-5 hover:border-mindora-red transition-colors">
              <BookOpen size={18} className="text-mindora-red" />
              <div className="font-serif text-xl text-mindora-ink mt-2">Share a story</div>
              <div className="text-xs text-mindora-muted mt-1">Tell the truth — even just a line.</div>
            </Link>
            <Link to="/empathy" data-testid="dashboard-quick-empathy" className="rounded-2xl border border-mindora-line p-5 hover:border-mindora-red transition-colors">
              <Heart size={18} className="text-mindora-red" />
              <div className="font-serif text-xl text-mindora-ink mt-2">Empathy exercise</div>
              <div className="text-xs text-mindora-muted mt-1">10 minutes. Concrete. Doable.</div>
            </Link>
            <Link to="/gratitude" data-testid="dashboard-quick-gratitude" className="rounded-2xl border border-mindora-line p-5 hover:border-mindora-red transition-colors">
              <Sparkles size={18} className="text-mindora-red" />
              <div className="font-serif text-xl text-mindora-ink mt-2">One gratitude</div>
              <div className="text-xs text-mindora-muted mt-1">No matter how small. Especially small.</div>
            </Link>
          </div>
        </div>

        {/* Recent stories */}
        <div className="md:col-span-12 rounded-3xl bg-mindora-panel border border-mindora-line p-8 shadow-clay">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
              <BookOpen size={14} /> Recent in the feed
            </div>
            <Link to="/feed" className="text-sm text-mindora-red hover:underline">see all →</Link>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-stories">
            {stories.map((s) => (
              <Link key={s.id} to={`/story/${s.id}`} className="rounded-2xl bg-mindora-bg border border-mindora-line p-5 hover:border-mindora-red transition-colors">
                <div className="text-[10px] uppercase tracking-[0.2em] text-mindora-red">{s.tag}</div>
                <div className="font-serif text-xl text-mindora-ink mt-1.5 leading-snug line-clamp-2">{s.title}</div>
                <div className="text-xs text-mindora-muted mt-2 flex items-center gap-1.5">
                  <Heart size={11} /> {s.relate_count} · {s.author_name}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
